import { Box, Button, Flex } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonTextInput } from 'components/common/form/text';
import { CommonProgress } from 'components/common/progress';
import { LandingFormWrapper } from 'components/main/landing/forms/wrapper';
import { IKeyUpEvent } from 'interfaces/forms/event';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ILoginPayload } from 'lib_ts/interfaces/common/i-login';
import { useCallback, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';

export const LoginFormLegacy = () => {
  const loading = useAuthStore((s) => s.loading);

  const [login, setLogin] = useState<ILoginPayload>({
    email: '',
    password: '',
  });

  const submit = useCallback(async () => {
    if (!login.email) {
      NotifyHelper.warning({
        message_md: 'Please enter your username and try again.',
      });
      return;
    }

    if (!login.password) {
      NotifyHelper.warning({
        message_md: 'Please enter your password and try again.',
      });
      return;
    }

    const result = await useAuthStore.getState().loginLegacy(login);

    if (result.error) {
      NotifyHelper.warning({
        message_md: result.error.message,
      });
      return;
    }
  }, [login]);

  const onKeyUp = useCallback(
    (event: IKeyUpEvent) => {
      if (event.code === 'Enter') {
        void submit();
      }
    },
    [submit]
  );

  const renderContent = useCallback(() => {
    if (loading) {
      return <CommonProgress value={80} color={RADIX.COLOR.INFO} />;
    }

    return (
      <CommonFormGrid columns={1}>
        <CommonTextInput
          id="legacy-login-email"
          value={login.email}
          type="email"
          placeholder="user@trajektsports.com"
          onKeyUp={onKeyUp}
          autoComplete="username"
          disabled={loading}
          onChange={(v) =>
            setLogin((prev) => ({
              ...prev,
              email: v ?? '',
            }))
          }
        />

        <CommonTextInput
          id="legacy-login-password"
          value={login.password}
          type="password"
          controlId="password"
          placeholder="*********"
          onKeyUp={onKeyUp}
          autoComplete="current-password"
          disabled={loading}
          onChange={(v) =>
            setLogin((prev) => ({
              ...prev,
              password: v ?? '',
            }))
          }
        />

        <Box>
          <Button
            className="btn-block"
            color={RADIX.COLOR.SUCCESS}
            disabled={loading}
            onClick={submit}
          >
            Login
          </Button>
        </Box>
      </CommonFormGrid>
    );
  }, [loading, login.email, login.password, onKeyUp, submit]);

  return (
    <ErrorBoundary identifier="LoginFormLegacy" hideIntercom>
      <LandingFormWrapper
        header={
          loading ? 'Please wait...' : 'Please log in to access your machine.'
        }
      >
        <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
          {renderContent()}
        </Flex>
      </LandingFormWrapper>
    </ErrorBoundary>
  );
};
